import { Box } from '@nrx/ui';
import type { JSX } from 'react';
import { useMemo } from 'react';
import type { DayButtonProps } from 'react-day-picker';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import 'react-day-picker/style.css';

import type { GroupConsultDayBucket } from './group-consult-calendar.js';
import {
  dateFromGroupConsultDayKey,
  formatGroupConsultDayHeading,
} from './group-consult-calendar.js';

type GroupConsultMonthViewProps = {
  dayBuckets: GroupConsultDayBucket[];
  selectedDayKey: string | null;
  visibleMonth: Date;
  onSelectDayKey: (dayKey: string | null) => void;
  onVisibleMonthChange: (month: Date) => void;
};

/** Render the alternate month calendar with highlighted consult days. */
export function GroupConsultMonthView({
  dayBuckets,
  selectedDayKey,
  visibleMonth,
  onSelectDayKey,
  onVisibleMonthChange,
}: GroupConsultMonthViewProps): JSX.Element {
  const defaultClassNames = useMemo(() => getDefaultClassNames(), []);
  const dayBucketByKey = useMemo(
    () => new Map(dayBuckets.map((bucket) => [bucket.dayKey, bucket])),
    [dayBuckets],
  );
  const selectedDay = selectedDayKey ? (dayBucketByKey.get(selectedDayKey) ?? null) : null;
  const selectedDate = selectedDayKey ? dateFromGroupConsultDayKey(selectedDayKey) : undefined;
  const currentMonth = startOfMonth(new Date());
  const earliestMonth = startOfMonth(dayBuckets[0]?.date ?? currentMonth);
  const latestMonth = startOfMonth(dayBuckets[dayBuckets.length - 1]?.date ?? currentMonth);
  const startMonth = minMonth(earliestMonth, currentMonth);
  const endMonth = maxMonth(latestMonth, addMonths(currentMonth, 2));

  const AvailabilityDayButton = useMemo(() => {
    function AvailabilityDayButtonComponent({
      day,
      modifiers,
      className,
      children,
      onClick,
      ...buttonProps
    }: DayButtonProps) {
      const dayKey = getLocalDayKey(day.date);
      const bucket = dayBucketByKey.get(dayKey);
      const baseAriaLabel =
        typeof buttonProps['aria-label'] === 'string' ? buttonProps['aria-label'] : '';
      const availabilityLabel = bucket
        ? `${bucket.events.length} group consult session${
            bucket.events.length === 1 ? '' : 's'
          } available. ${bucket.titles.join(', ')}.`
        : '';

      return (
        <div className="group-consult-day-cell">
          <DayButton
            {...buttonProps}
            day={day}
            modifiers={modifiers}
            aria-label={[baseAriaLabel, availabilityLabel].filter(Boolean).join(' ')}
            onClick={(event) => {
              onClick?.(event);
              if (bucket) {
                onSelectDayKey(dayKey);
              }
            }}
            className={[
              className,
              'group-consult-day',
              modifiers['selected']
                ? 'group-consult-day--selected'
                : bucket
                  ? 'group-consult-day--available'
                  : 'group-consult-day--empty',
              modifiers['today'] && !modifiers['selected'] ? 'group-consult-day--today' : '',
              modifiers['disabled'] && !bucket ? 'group-consult-day--disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </DayButton>
        </div>
      );
    }

    AvailabilityDayButtonComponent.displayName = 'AvailabilityDayButton';
    return AvailabilityDayButtonComponent;
  }, [dayBucketByKey, onSelectDayKey]);

  return (
    <Box as="section" className="group-consult-calendar-card">
      <DayPicker
        mode="single"
        month={visibleMonth}
        onMonthChange={(month) => {
          onVisibleMonthChange(new Date(month.getFullYear(), month.getMonth(), 1));
        }}
        selected={selectedDate}
        disabled={(date) => !dayBucketByKey.has(getLocalDayKey(date))}
        startMonth={startMonth}
        endMonth={endMonth}
        fixedWeeks
        classNames={{
          ...defaultClassNames,
          root: `${defaultClassNames.root} group-consult-calendar`,
          months: `${defaultClassNames.months} group-consult-calendar__months`,
          month: `${defaultClassNames.month} group-consult-calendar__month`,
          month_caption: `${defaultClassNames.month_caption} group-consult-calendar__caption`,
          caption_label: `${defaultClassNames.caption_label} group-consult-calendar__caption-label`,
          nav: `${defaultClassNames.nav} group-consult-calendar__nav`,
          button_previous: `${defaultClassNames.button_previous} group-consult-calendar__nav-button`,
          button_next: `${defaultClassNames.button_next} group-consult-calendar__nav-button`,
          weekdays: `${defaultClassNames.weekdays} group-consult-calendar__weekdays`,
          weekday: `${defaultClassNames.weekday} group-consult-calendar__weekday`,
          month_grid: `${defaultClassNames.month_grid} group-consult-calendar__grid`,
          week: `${defaultClassNames.week} group-consult-calendar__week`,
          day: `${defaultClassNames.day} group-consult-calendar__day`,
        }}
        components={{ DayButton: AvailabilityDayButton }}
        footer={
          selectedDay
            ? `${selectedDay.events.length} session${
                selectedDay.events.length === 1 ? '' : 's'
              } on ${formatGroupConsultDayHeading(selectedDay.date)}`
            : 'Select a highlighted day to view available sessions. You can browse ahead even when a month has no consults yet.'
        }
      />
    </Box>
  );
}

function getLocalDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function maxMonth(left: Date, right: Date): Date {
  return left.getTime() >= right.getTime() ? left : right;
}

function minMonth(left: Date, right: Date): Date {
  return left.getTime() <= right.getTime() ? left : right;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
